import {
  formatGbp,
  linesWithCode,
  profitOrLossLabel,
  profitOrLossText,
  type TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { Icon } from '@/kit/Icon';
import { displayDate } from './trialBalanceEditor';

interface TrialBalanceOverviewProps {
  model: TrialBalanceEditModel | null;
  /**
   * Changes whenever the shared model changes. The model object is mutated in place, so the parent re-renders this
   * panel by bumping it (Angular `ngOnChanges`); it is only read for that.
   */
  revision?: number;
  /** APT354: Import Balances sits beside the Period (Overview only; Edit Details has no import). */
  onImportBalances?: () => void;
}

/**
 * Angular TrialBalanceOverviewComponent: the read-only summary tab (the figures are TrialBalanceSummary, fixed top-right - APT347).
 * APT354: the Description joins Client / Reference / Period here (it no longer repeats under the tabs), and Import Balances is
 * beside the Period.
 */
export function TrialBalanceOverview({ model, onImportBalances }: TrialBalanceOverviewProps) {
  let periodText = '';
  if (model) {
    const from = displayDate(model.fromDate);
    const to = displayDate(model.toDate);
    periodText = model.periodLabel || (from && to ? `${from} - ${to}` : '');
  }

  return (
    <div className="tb-info-cards">
      <div className="tb-info-card">
        <div className="tb-info-card__label">Client</div>
        <div className="tb-info-card__value">{model?.clientName}</div>
      </div>
      <div className="tb-info-card">
        <div className="tb-info-card__label">Reference</div>
        <div className="tb-info-card__value">{model?.refNo}</div>
      </div>
      <div className="tb-info-card">
        <div className="tb-info-card__label">Description</div>
        <div className="tb-info-card__value">{model?.description || '-'}</div>
      </div>
      <div className="tb-info-card tb-info-card--period">
        <div className="tb-info-card__label">Period</div>
        <div className="tb-info-card__value">{periodText}</div>
        {onImportBalances && (
          <a className="link text-underline-none d-flex align-items-center" onClick={onImportBalances}>
            <Icon name="file_download" className="font-size-18" />
            Import Balances
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * The Lines, Total Dr., Total Cr., Profit or Loss and Status figures shown as a single divided strip above the grid
 * (same figures the Overview showed before, Lines/Total Dr./Total Cr./Profit/Status).
 */
export function TrialBalanceSummary({ model }: TrialBalanceOverviewProps) {
  const lineCount = model ? linesWithCode(model).length : 0;
  const totalDebitText = model ? formatGbp(model.totalDebit) : formatGbp(0);
  const totalCreditText = model ? formatGbp(model.totalCredit) : formatGbp(0);
  const profitLabel = model ? profitOrLossLabel(model.profit) : 'Profit';
  const profitText = model ? profitOrLossText(model.profit) : formatGbp(0);
  // Ticket "Saving TB": the totals are no longer repeated under the lines, so the side and size of a difference shows here.
  const difference = model ? model.totalDebit - model.totalCredit : 0;
  const differenceText = `${difference > 0 ? 'Debit' : 'Credit'} ${formatGbp(Math.abs(difference))}`;

  return (
    <div className="tb-summary" data-testid="tb-summary">
      <div className="tb-summary__cell">
        <div className="tb-summary__label">Lines</div>
        <div className="tb-summary__value">{lineCount}</div>
      </div>
      <div className="tb-summary__cell">
        <div className="tb-summary__label">Total Dr.</div>
        <div className="tb-summary__value">{totalDebitText}</div>
      </div>
      <div className="tb-summary__cell">
        <div className="tb-summary__label">Total Cr.</div>
        <div className="tb-summary__value">{totalCreditText}</div>
      </div>
      <div className="tb-summary__cell">
        <div className="tb-summary__label">{profitLabel}</div>
        <div className="tb-summary__value">{profitText}</div>
      </div>
      <div className="tb-summary__cell tb-summary__cell--status">
        <div className="tb-summary__label">Status</div>
        <div className="tb-summary__value d-flex gap-5">
          <span className={model?.isBalanced ? 'text-green' : 'text-red'}>
            {model?.isBalanced ? 'Balanced' : 'Out of balance'}
          </span>
          {model && !model.isBalanced && <span className="text-red">{differenceText}</span>}
        </div>
      </div>
    </div>
  );
}
